import { batch, computed, effect, signal } from "@preact/signals";

import LatestRequest from "LatestRequest";
import readlangClient from "readlangClient";
import SelectionStore from "stores/SelectionStore";
import UserStore from "stores/UserStore";
import UserWordStore, { UserWordData } from "stores/UserWordStore";

interface RemoteUserWordsOptions {
  user: UserStore;
  suppressFetch?: boolean;
}

/**
 * There is deliberately no error callback: a failed fetch rejects the promise
 * returned by fetch()/reFetch() (readlangClient has already shown the user a
 * notification by then), so callers that care handle it with try/catch.
 */
interface FetchOptions {
  data?: Record<string, any>;
  success?: (error: null, result: UserWordsResult) => void;
}

interface UserWordsResult {
  count: number;
  userWords: UserWordData[];
}

export interface UserWordsSearchQuery {
  limit: number;
  contexts: boolean;
  sortBy: string;
  [key: string]: any;
}

const defaultSearchQuery = (): UserWordsSearchQuery => ({
  limit: 25,
  contexts: true,
  sortBy: "lastModified",
});

export default class RemoteUserWordsStore {
  readonly words = signal<UserWordStore[]>([]);
  readonly totalCountSignal = signal(0);
  /** Whether more words are being loaded, below those shown */
  readonly loadingMore = signal(false);
  readonly searchQuery = signal<UserWordsSearchQuery>(defaultSearchQuery());
  /**
   * The search query the loaded words were fetched with: until a new query's
   * words arrive, the old words are still shown (and loaded more of) as theirs
   */
  readonly loadedSearchQuery = signal<UserWordsSearchQuery>(
    this.searchQuery.value
  );
  readonly wordsDeleted = signal<{
    numDeleted: number;
    deletedDate: string;
  } | null>(null);
  readonly viewJSON = computed(() =>
    this.words.value.map((word) => word.viewJSON.value)
  );
  readonly selectionStore: SelectionStore;

  readonly url = "/api/v2/userWords";

  private options: RemoteUserWordsOptions;
  private wordDisposers = new Map<string, Array<() => void>>();
  private translationChangeCallbacks = new Set<
    (
      word: UserWordStore,
      translation: string | undefined,
      previousTranslation: string | undefined
    ) => void
  >();
  private wordsDeletedCallbacks = new Set<(wordIDs: string[]) => void>();
  private suppressFetch = false;
  private readonly latestFetch = new LatestRequest();
  /** Bumped whenever the words are replaced, so a late fetchMore is dropped */
  private resetCount = 0;

  constructor(words: UserWordData[] = [], options: RemoteUserWordsOptions) {
    this.options = options;
    this.selectionStore = new SelectionStore(this);
    this.reset(words);

    this.onSearchQueryChange(() => {
      if (this.suppressFetch) return;
      this.selectionStore.clearSelection();
      if (this.searchQuery.value.wordGroup === "deleted") {
        this.patchSearchQuery(
          { sortBy: "deletedDate" },
          { suppressFetch: true }
        );
      }
      this.reFetch();
    });
  }

  get length() {
    return this.words.value.length;
  }

  get totalCount() {
    return this.totalCountSignal.value;
  }

  set totalCount(count: number) {
    this.totalCountSignal.value = count;
  }

  firstWord() {
    return this.words.value[0];
  }

  getWord(id: string) {
    return this.words.value.find((word) => word.id === id);
  }

  getWordIDs() {
    return this.words.value.map((word) => String(word.id));
  }

  getFieldValues(attr: string) {
    return this.words.value.map((word) => word.state.value[attr]);
  }

  getSelectionQuery() {
    return { ...this.searchQuery.value };
  }

  patchSearchQuery(
    patch: Partial<UserWordsSearchQuery>,
    options: { suppressFetch?: boolean } = {}
  ) {
    const previousSuppressFetch = this.suppressFetch;
    this.suppressFetch =
      previousSuppressFetch || Boolean(options.suppressFetch);
    this.searchQuery.value = { ...this.searchQuery.value, ...patch };
    this.suppressFetch = previousSuppressFetch;
  }

  resetSearchQuery(patch: Partial<UserWordsSearchQuery> = {}) {
    const previousSuppressFetch = this.suppressFetch;
    this.suppressFetch = true;
    this.searchQuery.value = { ...defaultSearchQuery(), ...patch };
    this.suppressFetch = previousSuppressFetch;
  }

  onSearchQueryChange(callback: () => void) {
    let previous = this.searchQuery.value;
    return effect(() => {
      const current = this.searchQuery.value;
      if (current !== previous) {
        previous = current;
        callback();
      }
    });
  }

  onTranslationChange(
    callback: (
      word: UserWordStore,
      translation: string | undefined,
      previousTranslation: string | undefined
    ) => void
  ) {
    this.translationChangeCallbacks.add(callback);
    return () => {
      this.translationChangeCallbacks.delete(callback);
    };
  }

  /** Observes words being deleted: the ids of those loaded here */
  onWordsDeleted(callback: (wordIDs: string[]) => void) {
    this.wordsDeletedCallbacks.add(callback);
    return () => {
      this.wordsDeletedCallbacks.delete(callback);
    };
  }

  private notifyWordsDeleted(words: Array<UserWordStore | undefined>) {
    const wordIDs = words
      .map((word) => word?.id)
      .filter((id): id is string => Boolean(id));
    for (const callback of this.wordsDeletedCallbacks) {
      callback(wordIDs);
    }
  }

  onChange(
    callback: (words: UserWordStore[], previousWords: UserWordStore[]) => void
  ) {
    let previousWords = this.words.value;
    return effect(() => {
      const currentWords = this.words.value;
      if (currentWords !== previousWords) {
        const oldWords = previousWords;
        previousWords = currentWords;
        callback(currentWords, oldWords);
      }
    });
  }

  reset(words: UserWordData[] = [], searchQuery = this.searchQuery.value) {
    this.resetCount++;
    this.detachAllWords();
    const stores = words.map((word) => new UserWordStore(word));
    batch(() => {
      this.loadedSearchQuery.value = searchQuery;
      this.words.value = stores;
    });
    for (const word of stores) {
      this.attachWord(word);
    }
  }

  addUserWord(
    word: UserWordStore | UserWordData,
    options: { merge?: boolean; at?: number } = {}
  ) {
    const wordStore =
      word instanceof UserWordStore ? word : new UserWordStore(word);
    const existing = wordStore.id ? this.getWord(wordStore.id) : undefined;

    if (existing && options.merge) {
      existing.patch(wordStore.viewJSON.value);
      if (options.at !== undefined) {
        this.words.value = [
          existing,
          ...this.words.value.filter((item) => item !== existing),
        ];
      }
      return existing;
    }

    const next = [...this.words.value];
    if (options.at !== undefined) {
      next.splice(options.at, 0, wordStore);
    } else {
      next.push(wordStore);
    }
    this.words.value = next;
    this.totalCountSignal.value++;
    this.attachWord(wordStore);
    return wordStore;
  }

  remove(words: UserWordStore | Array<UserWordStore | undefined>) {
    const wordsToRemove = Array.isArray(words) ? words : [words];
    const idsToRemove = new Set(
      wordsToRemove.filter(Boolean).map((word) => word!.id)
    );
    if (idsToRemove.size === 0) return;

    const removed = this.words.value.filter((word) => idsToRemove.has(word.id));
    this.words.value = this.words.value.filter(
      (word) => !idsToRemove.has(word.id)
    );
    this.totalCountSignal.value = Math.max(
      0,
      this.totalCountSignal.value - removed.length
    );
    for (const word of removed) {
      this.detachWord(word);
      this.selectionStore.removeItem(String(word.id));
    }
  }

  async performAction(action: string, selectionStore: SelectionStore) {
    const selectedWords = selectionStore.wholeCollection.value
      ? this.words.value
      : selectionStore.itemIDs.value.map((userWordID) =>
          this.getWord(userWordID)
        );

    switch (action) {
      case "delete": {
        const { data } = await readlangClient.request<any>(
          "DELETE",
          "/api/userWords",
          selectionStore.getQuery()
        );
        this.remove(selectedWords);
        this.selectionStore.clearSelection();
        this.wordsDeleted.value = data;
        this.notifyWordsDeleted(selectedWords);
        break;
      }
      case "undelete":
        await readlangClient.request(
          "PATCH",
          "/api/userWords/unDelete",
          selectionStore.getQuery()
        );
        this.remove(selectedWords);
        this.selectionStore.clearSelection();
        break;
      case "permDelete":
        await this.permanentlyDeleteWords(selectionStore, selectedWords);
        break;
      default:
        console.error("action not recognised: ", action);
    }
  }

  hasMore() {
    return this.length < this.totalCount;
  }

  /**
   * Loads the words after those already loaded (infinite scroll), returning
   * whether it added any
   */
  async fetchMore() {
    if (
      this.options.suppressFetch ||
      this.loadingMore.value ||
      !this.hasMore()
    ) {
      return false;
    }
    const resetCount = this.resetCount;
    this.loadingMore.value = true;
    try {
      const { data: result } = await readlangClient.request<UserWordsResult>(
        "GET",
        this.url,
        {
          ...this.loadedSearchQuery.value,
          from: this.length,
          language: this.options.user.state.value.currentLearningLanguage,
        }
      );
      if (resetCount !== this.resetCount) {
        return false;
      }
      this.totalCount = result.count;
      const added = result.userWords
        .filter((word) => !this.getWord(String(word._id)))
        .map((word) => new UserWordStore(word));
      this.words.value = [...this.words.value, ...added];
      for (const word of added) {
        this.attachWord(word);
      }
      // "All loaded words" (or all words) stays true of the longer list
      const selection = this.selectionStore;
      if (selection.wholePage.value || selection.wholeCollection.value) {
        selection.addItems(added.map((word) => String(word.id)));
      }
      return added.length > 0;
    } finally {
      this.loadingMore.value = false;
    }
  }

  fetch(params: FetchOptions = {}) {
    this.resetSearchQuery(params.data ?? {});

    return this.reFetch(params);
  }

  async addWord(
    userWord: UserWordStore,
    callback?: (word: UserWordStore) => void
  ) {
    const { data: result } = await readlangClient.request<any>(
      "POST",
      "/api/userWord",
      // every word added here comes from a click to translate while reading,
      // which the server counts as a failed recall if the word was already
      // saved and scheduled for review
      { ...userWord.viewJSON.value, lookedUpWhileReading: true }
    );

    if (!result.success) {
      console.error("create userWord failed");
      return;
    }

    this.options.user.markLanguageActive(userWord.state.value.language);

    if (result.frequency) {
      userWord.patch({ frequency: result.frequency });
    }

    if (
      userWord.state.value.language !==
      this.options.user.state.value.currentLearningLanguage
    ) {
      return;
    }

    const sortBy = this.searchQuery.value.sortBy;
    const isUpdate = Boolean(result.updated);
    const insertAtTop =
      sortBy === "lastModified" || (sortBy === "new" && !isUpdate);
    const addOptions: { merge: true; at?: number } = { merge: true };
    if (insertAtTop) {
      addOptions.at = 0;
    }

    if (isUpdate) {
      const existing = this.getWord(result.updated);
      if (existing) {
        this.remove(existing);
      }

      const fetchedWord = new UserWordStore({ _id: result.updated });
      await fetchedWord.fetch();
      this.addUserWord(fetchedWord, addOptions);
      callback?.(fetchedWord);
    } else {
      if (!userWord.hasField("_id")) {
        userWord.patch({ _id: result.updated || result.upserted });
      }
      this.addUserWord(userWord, addOptions);
      callback?.(userWord);
    }
  }

  async reFetch(params: FetchOptions = {}) {
    if (this.options.suppressFetch || this.suppressFetch) {
      return;
    }

    const searchQuery = this.searchQuery.value;
    const response = await this.latestFetch.send(
      readlangClient.request<UserWordsResult>("GET", this.url, {
        ...searchQuery,
        language: this.options.user.state.value.currentLearningLanguage,
      })
    );
    if (!response) {
      return;
    }
    const result = response.data;
    batch(() => {
      this.totalCount = result.count;
      this.reset(result.userWords, searchQuery);
    });

    params.success?.(null, result);
    return result;
  }

  private async permanentlyDeleteWords(
    selectionStore: SelectionStore,
    selectedWords: Array<UserWordStore | undefined>
  ) {
    const wordCount = selectionStore.count.value;
    const countString =
      wordCount === 1 ? "this word" : "these " + wordCount + " words";
    if (
      confirm(
        "Are you sure you want to PERMANENTLY DELETE " +
          countString +
          ". No way to undo!"
      )
    ) {
      await readlangClient.request("DELETE", "/api/userWords", {
        ...selectionStore.getQuery(),
        permanent: true,
      });
      this.remove(selectedWords);
      this.selectionStore.clearSelection();
    }
  }

  private attachWord(word: UserWordStore) {
    if (!word.id) return;

    const disposers = [
      word.onChange(
        (data) => data,
        () => {
          this.words.value = [...this.words.value];
        }
      ),
      word.onChange(
        (data) => data.translation,
        (translation, previousTranslation) => {
          for (const callback of this.translationChangeCallbacks) {
            callback(word, translation, previousTranslation);
          }
        }
      ),
      effect(() => {
        void word.deletedSequence.value;
        if (word.deletedSequence.value > 0) {
          this.remove(word);
          this.notifyWordsDeleted([word]);
        }
      }),
    ];
    this.wordDisposers.set(word.id, disposers);
  }

  private detachWord(word: UserWordStore) {
    const id = word.id;
    if (!id) return;
    const disposers = this.wordDisposers.get(id) ?? [];
    for (const dispose of disposers) {
      dispose();
    }
    this.wordDisposers.delete(id);
  }

  private detachAllWords() {
    for (const word of this.words.value) {
      this.detachWord(word);
    }
  }
}
